"use client";

import { useEffect, useRef, useState } from "react";

type Creator = {
  id: string;
  name: string;
  category: string;
  avatar: string;
};

type Props = {
  creators: Creator[];
  isOpen: boolean;
  onSelect: (_id: string) => void;
  onClose: () => void;
};

type CreatorCategory = "all" | "監督" | "漫画家" | "アニメーター" | "画家";

const CREATOR_CATEGORIES: { label: string; value: CreatorCategory }[] = [
  { label: "すべて", value: "all" },
  { label: "監督", value: "監督" },
  { label: "漫画家", value: "漫画家" },
  { label: "アニメーター", value: "アニメーター" },
  { label: "画家", value: "画家" },
];

const tabActiveClass =
  "border-[#A594F9]/60 bg-gradient-to-br from-[#A594F9]/20 to-[#A594F9]/10 text-[#A594F9] shadow-[0_4px_15px_rgba(165,148,249,0.3)]";
const tabInactiveClass =
  "border-[#A594F9]/30 bg-white/5 text-[#8b92a8] hover:border-[#A594F9]/50 hover:bg-white/10 hover:text-white";

export default function CreatorSelectModal({ creators, isOpen, onSelect, onClose }: Props) {
  const [activeCategory, setActiveCategory] = useState<CreatorCategory>("all");
  const tabRowRef = useRef<HTMLDivElement>(null);
  const [showTabLeftFade, setShowTabLeftFade] = useState(false);
  const [showTabRightFade, setShowTabRightFade] = useState(false);
  const isDraggingRef = useRef(false);
  const dragStartXRef = useRef(0);
  const dragStartScrollRef = useRef(0);
  const hasDraggedRef = useRef(false);

  const handleTabRowScroll = () => {
    const el = tabRowRef.current;
    if (!el) return;
    setShowTabLeftFade(el.scrollLeft > 5);
    setShowTabRightFade(el.scrollLeft < el.scrollWidth - el.clientWidth - 5);
  };

  const handleTabMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
    const el = tabRowRef.current;
    if (!el) return;
    isDraggingRef.current = true;
    hasDraggedRef.current = false;
    dragStartXRef.current = e.clientX;
    dragStartScrollRef.current = el.scrollLeft;
    el.style.cursor = "grabbing";
  };

  const handleTabMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!isDraggingRef.current) return;
    const el = tabRowRef.current;
    if (!el) return;
    const delta = e.clientX - dragStartXRef.current;
    if (Math.abs(delta) > 5) hasDraggedRef.current = true;
    el.scrollLeft = dragStartScrollRef.current - delta;
  };

  const handleTabMouseUp = () => {
    isDraggingRef.current = false;
    const el = tabRowRef.current;
    if (el) el.style.cursor = "";
  };

  useEffect(() => {
    const el = tabRowRef.current;
    if (!el) return;
    const check = () => setShowTabRightFade(el.scrollWidth > el.clientWidth + 5);
    check();
    const observer = new ResizeObserver(check);
    observer.observe(el);
    return () => observer.disconnect();
  }, [isOpen]);

  //----------
  // モーダルが開いている間、背景ページのスクロールを無効化する
  //----------
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [isOpen]);

  if (!isOpen) return null;

  const filteredCreators =
    activeCategory === "all" ? creators : creators.filter((c) => c.category === activeCategory);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="mx-4 flex h-[min(70vh,520px)] w-full max-w-lg flex-col rounded-2xl border border-white/10 bg-[#1a1a2e] p-6 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-4 flex justify-end">
          <button
            type="button"
            onClick={onClose}
            className="text-white/50 transition-colors hover:text-white"
          >
            ✕
          </button>
        </div>

        {/* カテゴリタブ */}
        <div className="relative mb-4">
          <div
            className={`pointer-events-none absolute left-0 top-0 z-10 h-full w-8 bg-gradient-to-r from-[#1a1a2e] to-transparent transition-opacity duration-200 ${
              showTabLeftFade ? "opacity-100" : "opacity-0"
            }`}
          />
          <div
            className={`pointer-events-none absolute right-0 top-0 z-10 h-full w-8 bg-gradient-to-l from-[#1a1a2e] to-transparent transition-opacity duration-200 ${
              showTabRightFade ? "opacity-100" : "opacity-0"
            }`}
          />
          <div
            ref={tabRowRef}
            onScroll={handleTabRowScroll}
            onMouseDown={handleTabMouseDown}
            onMouseMove={handleTabMouseMove}
            onMouseUp={handleTabMouseUp}
            onMouseLeave={handleTabMouseUp}
            className="flex cursor-grab select-none gap-2 overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
          >
            {CREATOR_CATEGORIES.map(({ label, value }) => (
              <button
                key={value}
                type="button"
                onClick={() => {
                  if (hasDraggedRef.current) return;
                  setActiveCategory(value);
                }}
                className={`shrink-0 cursor-pointer rounded-[25px] border px-4 py-1.5 text-xs font-medium backdrop-blur-[10px] transition-all duration-300 ${
                  activeCategory === value ? tabActiveClass : tabInactiveClass
                }`}
              >
                {label}
              </button>
            ))}
          </div>
        </div>

        <ul className="flex flex-1 flex-col gap-2 overflow-y-auto pr-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
          {filteredCreators.map((creator) => (
            <li key={creator.id}>
              <button
                type="button"
                onClick={() => onSelect(creator.id)}
                className="flex w-full items-center rounded-xl border border-white/5 bg-white/5 px-4 py-3 text-left transition-colors hover:border-PRIMARY/50 hover:bg-white/10"
              >
                <div>
                  <p className="text-sm font-medium text-white">{creator.name}</p>
                  <p className="text-xs text-white/50">{creator.category}</p>
                </div>
              </button>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}
